const LOCAL_API = "http://localhost:1337";
const LOCAL_TICKETING_APP = "http://localhost:3001";
const LOCAL_TICKETING_API = "http://localhost:4000";

function trimTrailingSlash(url: string): string {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

function readRuntimeUrl(
  key:
    | "apiUrl"
    | "strapiUrl"
    | "cmsUrl"
    | "siteUrl"
    | "ticketingAppUrl"
    | "ticketingApiUrl",
): string | undefined {
  const value = window.__APP_CONFIG__?.[key]?.trim();
  return value ? trimTrailingSlash(value) : undefined;
}

function readEnvUrl(
  key:
    | "VITE_API_URL"
    | "VITE_STRAPI_URL"
    | "VITE_CMS_URL"
    | "VITE_SITE_URL"
    | "VITE_TICKETING_APP_URL"
    | "VITE_TICKETING_API_URL",
): string | undefined {
  const value = import.meta.env[key]?.trim();
  return value ? trimTrailingSlash(value) : undefined;
}

/** Avoid mixed-content blocks when the SPA is served over HTTPS. */
function ensureHttpsWhenPageIsSecure(url: string): string {
  if (typeof window === "undefined" || window.location.protocol !== "https:") {
    return url;
  }
  if (!/^http:\/\//i.test(url)) return url;

  try {
    const { hostname } = new URL(url);
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return url;
    }
  } catch {
    return url;
  }

  return url.replace(/^http:\/\//i, "https://");
}

/** Governance API base URL (Strapi /api/*). */
export function getApiUrl(): string {
  const url = readRuntimeUrl("apiUrl") ?? readEnvUrl("VITE_API_URL") ?? LOCAL_API;
  return ensureHttpsWhenPageIsSecure(url);
}

/** Strapi origin for admin links and media resolution. */
export function getStrapiUrl(): string {
  const url =
    readRuntimeUrl("strapiUrl") ??
    readEnvUrl("VITE_STRAPI_URL") ??
    readRuntimeUrl("apiUrl") ??
    readEnvUrl("VITE_API_URL") ??
    LOCAL_API;
  return ensureHttpsWhenPageIsSecure(url);
}

/** Strapi CMS admin origin (content manager links, SSO handoff). */
export function getCmsUrl(): string {
  const url =
    readRuntimeUrl("cmsUrl") ??
    readEnvUrl("VITE_CMS_URL") ??
    readRuntimeUrl("strapiUrl") ??
    readEnvUrl("VITE_STRAPI_URL") ??
    readRuntimeUrl("apiUrl") ??
    readEnvUrl("VITE_API_URL") ??
    LOCAL_API;
  return ensureHttpsWhenPageIsSecure(url);
}

/**
 * The PUBLIC SITE origin — where a report actually renders.
 *
 * Used to preview an OBS report before publishing it. That preview is the real
 * screen, drawn by the real renderer from the real uploaded data, which is only
 * possible because an unpublished report keeps resolving at its URL (it simply
 * stays off the tile grid). Duplicating the charts inside the dashboard to
 * approximate the same thing would be a second renderer to keep in step.
 *
 * Configure it explicitly — `siteUrl` in the runtime config the container writes,
 * or VITE_SITE_URL at build time. Both are checked before anything is derived.
 *
 * ⚠️ THIS USED TO RETURN UNDEFINED RATHER THAN GUESS, on the reasoning that the
 * dashboard, the CMS and the site are three different hosts with nothing safe to
 * derive from. The caution was right; the conclusion cost more than it saved. The
 * configuration was never wired up anywhere — not compose, not CI, not the box —
 * so the value was ALWAYS undefined in production, and the "safe" fallback showed
 * every operator an unclickable path they had to retype by hand. A guess that is
 * wrong costs one 404 and is obvious the moment it is clicked; silence cost the
 * feature entirely, and looked like it had never been built.
 *
 * So there is now one last resort, and it is narrow on purpose: ONLY a hostname
 * beginning `dashboard.` is rewritten, to `www.` on the same domain, which is how
 * every environment of this stack is laid out. Anything else — localhost, an IP,
 * a differently-named deployment — still yields undefined and still degrades to
 * the plain-text path, because there the guess really would be a guess.
 */
function deriveSiteUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;

  const { protocol, hostname } = window.location;
  const prefix = "dashboard.";
  if (!hostname.startsWith(prefix)) return undefined;

  // The www host, not the apex: the apex 301s, and a redirect behind
  // target="_blank" is a worse first impression than no link at all.
  return `${protocol}//www.${hostname.slice(prefix.length)}`;
}

export function getSiteUrl(): string | undefined {
  const url = readRuntimeUrl("siteUrl") ?? readEnvUrl("VITE_SITE_URL") ?? deriveSiteUrl();
  return url ? ensureHttpsWhenPageIsSecure(url) : undefined;
}

/** Support console frontend (mof-ticketing SPA). */
export function getTicketingAppUrl(): string {
  const url =
    readRuntimeUrl("ticketingAppUrl") ??
    readEnvUrl("VITE_TICKETING_APP_URL") ??
    LOCAL_TICKETING_APP;
  return ensureHttpsWhenPageIsSecure(url);
}

/** Ticketing NestJS API base URL (no /api suffix). Empty in dev → same-origin Vite proxy. */
export function getTicketingApiUrl(): string {
  const url =
    readRuntimeUrl("ticketingApiUrl") ??
    readEnvUrl("VITE_TICKETING_API_URL");
  if (url) return ensureHttpsWhenPageIsSecure(url);
  if (import.meta.env.DEV) return "";
  return ensureHttpsWhenPageIsSecure(LOCAL_TICKETING_API);
}
